import React, { FC, useRef, useState, useEffect } from 'react';
import {
  Input,
  Box,
  Text,
  Image,
  Flex,
  Button,
} from '@resideo/blueprint-react';
import Collapsible from 'react-collapsible';
import { CloseRoundGreyImg, downArrowIcon, upArrowIcon } from 'assets/images';
import styled from 'styled-components';
import { useTranslation, TFunction } from 'react-i18next';
import { STATES } from 'config/states';
import { InputField, SelectField } from 'components/common/InputField';
import { Container } from './BillingAddress';
import SeparatorLine from '../../common/SeparatorLine';
import Alert from 'components/common/Alert';
import { useToast } from 'components/common/Toast';
import moment from 'moment';
import { FormikErrors } from 'formik';

const CloseBtn = styled(Box)`
  cursor: pointer;
`;

const StyledCursorImage = styled(Image)`
  cursor: pointer;
`;

export interface TaxExemptionFormValues {
  companyName: string;
  taxAuthority: string;
  startDate: string;
  endDate: string;
}

export const validateTaxExemptionForm = (
  values: TaxExemptionFormValues,
  t: TFunction
) => {
  let errors: FormikErrors<TaxExemptionFormValues> | undefined;

  if ((values.companyName ?? '').trim().length === 0)
    errors = {
      ...errors,
      companyName: t('Company Name is Required'),
    };
  if ((values.taxAuthority ?? '').trim().length === 0)
    errors = {
      ...errors,
      taxAuthority: t('Tax Authority is Required'),
    };

  if ((values.startDate ?? '').trim().length === 0)
    errors = {
      ...errors,
      startDate: t('Start Date is Required'),
    };

  if ((values.endDate ?? '').trim().length === 0)
    errors = {
      ...errors,
      endDate: t('End Date is Required'),
    };

  return errors;
};

const TaxExemption: FC<any> = ({
  values,
  setFieldValue,
  formik,
  taxExempt,
  setTaxExempt,
  setFile,
  uploadedImg,
  setUploadedImg,
  setTaxInfoChanged,
  setTaxInfoComplete,
  dateValidated,
}) => {
  let myInput = useRef() as any;
  const { t } = useTranslation();
  const toast = useToast();

  // We need to check if there is session data and set state accordingly for logic to work
  const [startDate, setStartDate] = useState(
    localStorage.getItem('temp-tax-start-date')
      ? localStorage.getItem('temp-tax-start-date')
      : ''
  );
  const [endDate, setEndDate] = useState(
    localStorage.getItem('temp-tax-end-date')
      ? localStorage.getItem('temp-tax-end-date')
      : ''
  );
  const [companyName, setCompanyName] = useState(
    sessionStorage.getItem('temp-company-name')
      ? sessionStorage.getItem('temp-company-name')
      : ''
  );
  const [taxAuthority, setTaxAuthority] = useState(
    sessionStorage.getItem('temp-tax-state')
      ? sessionStorage.getItem('temp-tax-state')
      : ''
  );
  const [certificateUploaded, setCertificateUploaded] = useState('');
  const [taxInfoEntryInitiated, setTaxInfoEntryInitiated] = useState('false');

  const uploadImage = async e => {
    setCertificateUploaded('true'); // yes, string needed to trigger useEffect
    e.preventDefault();
    const file = e.target.files[0];
    setFile(file);
    setUploadedImg(file.name);
    setFieldValue('taxCertificate', file.name);
    localStorage.setItem('temp-file-name', file.name);
  };

  const reset = () => {
    myInput.value = '';
  };

  const resetFomikForm = () => {
    if (
      companyName === '' &&
      taxAuthority === '' &&
      certificateUploaded === '' &&
      startDate === '' &&
      endDate === ''
    ) {
      formik.setTouched({
        taxExemptionApplication: {
          companyName: false,
          taxAuthority: false,
          startDate: false,
          endDate: false,
        },
      });
    }
  };

  const updateErrorMessage = () => {
    formik.setTouched({
      taxExemptionApplication: {
        companyName: true,
        taxAuthority: true,
        startDate: true,
        endDate: true,
      },
    });
  };

  useEffect(() => {
    // Comprehensively checking to ensure all four values are present or not:
    if (
      (sessionStorage.getItem('temp-company-name') === null ||
        sessionStorage.getItem('temp-company-name') === '') &&
      (sessionStorage.getItem('temp-tax-state') === null ||
        sessionStorage.getItem('temp-tax-state') === '') &&
      (localStorage.getItem('temp-tax-start-date') === null ||
        localStorage.getItem('temp-tax-start-date') === '') &&
      (localStorage.getItem('temp-tax-end-date') === null ||
        localStorage.getItem('temp-tax-start-date') === '')
    ) {
      setTaxExempt(false);
    } else {
      setTaxExempt(true);
    }
  }, []);

  useEffect(() => {
    //* Form: Inactive State:
    if (
      companyName === '' &&
      taxAuthority === '' &&
      certificateUploaded === '' &&
      startDate === '' &&
      endDate === ''
    ) {
      setTaxInfoEntryInitiated('false');
      setTaxInfoComplete(true);
      // Notice we send data as if its complete to not disable review order button
    } else {
      setTaxInfoEntryInitiated('true');
    }
    //* Form: Active State:
    if (taxInfoEntryInitiated !== 'false') {
      if (
        startDate &&
        endDate &&
        companyName &&
        taxAuthority &&
        certificateUploaded
      ) {
        setTaxInfoComplete(true);
      } else {
        setTaxInfoComplete(false);
      }
    }
  }, [
    taxInfoEntryInitiated,
    certificateUploaded,
    companyName,
    taxAuthority,
    startDate,
    endDate,
  ]);

  const checkDates = endDate => {
    if (startDate != undefined) {
      if (startDate < endDate) {
        return true;
      }
    }
  };

  const checkDates2 = endDate => {
    const todaysDate = new Date();
    let day = todaysDate.getDate().toString();
    if (day.length === 1) {
      day = 0 + day;
    }
    let month = (todaysDate.getMonth() + 1).toString();
    if (month.length === 1) {
      month = 0 + month;
    }
    const year = todaysDate.getFullYear().toString();
    const todaysDateString = year + '-' + month + '-' + day;
    if (todaysDateString === endDate) {
      return false;
    } else {
      return true;
    }
  };

  const taxAuthorityUtilFn = state => {
    if (state !== 'Select a State') {
      sessionStorage.setItem('temp-tax-state', state);
      setTaxAuthority(state);
      setFieldValue('taxExemptionApplication.taxAuthority', state);
    } else {
      sessionStorage.setItem('temp-tax-state', '');
      setTaxAuthority('');
      setFieldValue('taxExemptionApplication.taxAuthority', '');
    }
  };

  return (
    <Box data-test-payment-sections>
      <Text
        data-test-section-title
        paddingY='medium'
        fontWeight='bold'
        fontSize='1.313rem'>
        {t('cart.billing.taxExemptionApplication')}
      </Text>
      <Container>
        <Collapsible
          open={taxExempt}
          onOpening={() => setTaxExempt(true)}
          onClosing={() => setTaxExempt(false)}
          trigger={
            <CloseBtn
              display={taxExempt ? 'none' : 'flex'}
              justifyContent='space-between'>
              <Text color='primary' fontWeight='500'>
                {taxExempt ? t('close') : t('cart.payment.expandTaxExemption')}
              </Text>
              <Image
                src={taxExempt ? upArrowIcon : downArrowIcon}
                alt='down-arrow'
              />
            </CloseBtn>
          }>
          <Text fontSize='small' paddingBottom='medium' fontStyle={'italic'}>
            {t('common.requiredFields')}
          </Text>
          <Box data-test-tax-inputs marginBottom='medium'>
            <InputField
              autoComplete='companyName'
              label={t('cart.billing.companyName')}
              name='taxExemptionApplication.companyName'
              //* A Quintuple ternary as it takes five operands
              //   It allows you to check for two values in a single ternary
              //    and can also be extended infinitely.  Cool Pattern 🚀
              value={
                sessionStorage.getItem('temp-company-name')
                  ? sessionStorage.getItem('temp-company-name')
                  : ''
              }
              onChange={e => {
                setTaxInfoEntryInitiated('true');
                setCompanyName(e.target.value);
                setFieldValue(
                  'taxExemptionApplication.companyName',
                  e.target.value
                );
                sessionStorage.setItem('temp-company-name', e.target.value);
                setTaxInfoChanged(true);
                !e.target.value
                  ? setTaxInfoChanged(false)
                  : setTaxInfoChanged(true);
              }}
              onBlur={() => {
                resetFomikForm();
              }}
              onFocus={() => {
                updateErrorMessage();
              }}
              error={
                formik.getFieldMeta(`taxExemptionApplication.companyName`)
                  .select &&
                formik.getFieldMeta(`taxExemptionApplication.companyName`).error
              }
              color={
                formik.getFieldMeta(`taxExemptionApplication.companyName`).error
                  ? 'redAlertError'
                  : ''
              }
            />
          </Box>
          <Box data-test-tax-inputs marginBottom='medium'>
            <SelectField
              name='taxExemptionApplication.taxAuthority'
              label={t('cart.billing.exemptionCertificate')}
              onChange={e => taxAuthorityUtilFn(e.target.value)}
              value={
                sessionStorage.getItem('temp-tax-state')
                  ? sessionStorage.getItem('temp-tax-state')
                  : values.taxExemptionApplication.taxAuthority
                  ? values.taxExemptionApplication.taxAuthority
                  : ''
              }
              onBlur={() => {
                resetFomikForm();
              }}
              onFocus={() => {
                updateErrorMessage();
              }}
              error={
                formik.getFieldMeta(`taxExemptionApplication.taxAuthority`)
                  .select &&
                formik.getFieldMeta(`taxExemptionApplication.taxAuthority`)
                  .error
              }
              color={
                formik.getFieldMeta(`taxExemptionApplication.taxAuthority`)
                  .error
                  ? 'redAlertError'
                  : ''
              }>
              {STATES.map((obj, i) => (
                <option disabled={obj.value === 'Select a State'} key={i}>
                  {obj.text}
                </option>
              ))}
            </SelectField>
          </Box>
          <Box data-test-tax-inputs marginBottom='medium'>
            <Flex display={'flex'} justifyContent={'space-between'}>
              <Box width={'calc(50% - 10px)'}>
                <InputField
                  autoComplete='off'
                  name='taxExemptionApplication.startDate'
                  label={t('cart.billing.exemptionStartDate')}
                  type='date'
                  value={
                    localStorage.getItem('temp-tax-start-date') ?? startDate
                  }
                  isDatePicker={true}
                  onChange={e => {
                    if (moment().isBefore(e.target.value)) {
                      toast(
                        <Alert variant='error'>
                          <Text>
                            &nbsp;The tax certificate start date must be before
                            today&rsquo;s date.
                          </Text>
                        </Alert>,
                        { duration: 10000 }
                      );
                    } else {
                      setFieldValue(
                        'taxExemptionApplication.startDate',
                        e.target.value
                      );
                      setStartDate(e.target.value);
                      localStorage.setItem(
                        'temp-tax-start-date',
                        e.target.value
                      );
                    }
                  }}
                  onBlur={() => {
                    resetFomikForm();
                  }}
                  onFocus={() => {
                    updateErrorMessage();
                  }}
                  error={
                    formik.getFieldMeta(`taxExemptionApplication.startDate`)
                      .select &&
                    formik.getFieldMeta(`taxExemptionApplication.startDate`)
                      .error
                  }
                  color={
                    formik.getFieldMeta(`taxExemptionApplication.startDate`)
                      .error
                      ? 'redAlertError'
                      : ''
                  }
                />
              </Box>
              <Box width={'calc(50% - 10px)'}>
                <InputField
                  name='taxExemptionApplication.endDate'
                  label={t('cart.billing.exemptionEndDate')}
                  type='date'
                  isDatePicker={true}
                  value={localStorage.getItem('temp-tax-end-date') ?? endDate}
                  onChange={e => {
                    setFieldValue(
                      'taxExemptionApplication.endDate',
                      e.target.value
                    );
                    if (e.target.value && !checkDates(e.target.value)) {
                      dateValidated(false);
                      toast(
                        <Alert variant='error'>
                          <Text>
                            &nbsp;The tax certificate end date must be greater
                            than the start date.
                          </Text>
                        </Alert>,
                        { duration: 10000 }
                      );
                    } else if (e.target.value && !checkDates2(e.target.value)) {
                      dateValidated(false);
                      toast(
                        <Alert variant='error'>
                          <Text>
                            &nbsp;The tax certificate end date cannot be todays
                            date.
                          </Text>
                        </Alert>,
                        { duration: 10000 }
                      );
                    } else {
                      dateValidated(true);
                      setEndDate(e.target.value);
                      localStorage.setItem('temp-tax-end-date', e.target.value);
                    }
                  }}
                  onBlur={() => {
                    resetFomikForm();
                  }}
                  onFocus={() => {
                    updateErrorMessage();
                  }}
                  error={
                    formik.getFieldMeta(`taxExemptionApplication.endDate`)
                      .select &&
                    formik.getFieldMeta(`taxExemptionApplication.endDate`).error
                  }
                  color={
                    formik.getFieldMeta(`taxExemptionApplication.endDate`).error
                      ? 'redAlertError'
                      : ''
                  }
                />
              </Box>
            </Flex>
          </Box>
          <Flex alignItems='center' paddingY='medium'>
            <Text fontSize={['small', 'medium']} fontWeight='500'>
              {t('cart.billing.uploadCertificate')}
            </Text>
            <Box marginLeft='small'>
              <Input
                id='myInput'
                name='file'
                style={{ display: 'none' }}
                type={'file'}
                ref={ref => (myInput = ref)}
                onChange={e => uploadImage(e)}
              />
              <Button
                data-test-shipping-add-new-address
                onClick={() => myInput.click()}
                variant='secondary'
                marginX='medium'
                paddingX='2.5rem'
                type='button'>
                {t('uploadFile')}
              </Button>
            </Box>
          </Flex>
          {uploadedImg && (
            <Box display='flex' justifyContent='flex-start' alignItems='center'>
              <Text color='gray' marginBottom='small'>
                {uploadedImg}
              </Text>
              <StyledCursorImage
                marginLeft='small'
                width='1rem'
                src={CloseRoundGreyImg}
                alt='close'
                onClick={() => {
                  setFile(null);
                  setUploadedImg('');
                  setCertificateUploaded('');
                  reset();
                }}
              />
            </Box>
          )}
          <Text fontFamily={'Roboto'} fontSize={'14px'}>
            {t('cart.payment.supportedFile')}
          </Text>
          {taxExempt && <SeparatorLine />}
          <CloseBtn
            onClick={() => {
              setTaxExempt(false);
              if (
                values.taxExemptionApplication.companyName &&
                (values.taxExemptionApplication.taxAuthority === '' ||
                  values.taxExemptionApplication.startDate === '' ||
                  values.taxExemptionApplication.endDate === '')
              ) {
                console.log('missing');
              }
            }}
            display={!taxExempt ? 'none' : 'flex'}
            justifyContent='space-between'>
            <Text color='primary' fontWeight='500'>
              {taxExempt ? t('close') : t('cart.payment.expandTaxExemption')}
            </Text>
            <Image
              src={taxExempt ? upArrowIcon : downArrowIcon}
              alt='down-arrow'
            />
          </CloseBtn>
        </Collapsible>
      </Container>
    </Box>
  );
};

export default TaxExemption;
